import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import { useTranslation } from 'react-i18next';

interface PermissionTemplate {
  id: string;
  template_code: string;
  template_name: string;
  description: string;
  is_system: boolean;
  schedule_view_departments?: string[];
  permissions?: Permission[];
}

interface Permission {
  id: string;
  permission_code: string;
  module: string;
  action: string;
  description: string;
}

const PermissionTemplates: React.FC = () => {
  const { t } = useTranslation();
  const { user, token, hasPermission, permissions } = useAuth();
  const [templates, setTemplates] = useState<PermissionTemplate[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [availableDepartments, setAvailableDepartments] = useState<string[]>([]);
  const [loading, setLoading] = useState(false); // Start as false, set to true only when fetching
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean; template: PermissionTemplate | null }>({
    open: false,
    template: null
  });
  
  // Form states
  const emptyTemplateForm = {
    templateCode: '',
    templateName: '',
    description: '',
    selectedPermissions: [] as string[],
    scheduleViewDepartments: [] as string[]
  };
  const [templateForm, setTemplateForm] = useState(emptyTemplateForm);

  const fetchData = useCallback(async () => {
    console.log('fetchData called');
    try {
      setLoading(true);
      setError(null);

      const [templatesRes, permissionsRes, departmentsRes] = await Promise.all([
        fetch(getApiUrl('/permissions/templates'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(getApiUrl('/permissions'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(getApiUrl('/permissions/schedule-departments'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        if (templatesData.success) {
          setTemplates(templatesData.templates || []);
        } else {
          setError(templatesData.message || t('permissions.templates.errors.failedToLoadTemplates'));
        }
      } else {
        const errorData = await templatesRes.json().catch(() => ({}));
        if (templatesRes.status === 403) {
          setError(t('permissions.templates.errors.noPermissionToViewTemplates'));
        } else {
          setError(errorData.message || t('permissions.templates.errors.failedToLoadTemplates'));
        }
      }

      if (departmentsRes.ok) {
        const departmentsData = await departmentsRes.json();
        if (departmentsData.success) {
          setAvailableDepartments(departmentsData.departments || []);
        }
      }

      if (permissionsRes.ok) {
        const permissionsData = await permissionsRes.json();
        if (permissionsData.success) {
          setAllPermissions(permissionsData.permissions || []);
        } else {
          console.error('Failed to load permissions:', permissionsData.message);
        }
      } else {
        const errorData = await permissionsRes.json().catch(() => ({}));
        console.error('Error loading permissions:', errorData.message || 'Failed to load permissions');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    console.log('PermissionTemplates useEffect:', {
      user: user?.email,
      // Role removed
      permissions: permissions,
      hasPermission: hasPermission('admin.permissions'),
      permissionsLength: permissions.length,
      token: token ? 'present' : 'missing'
    });
    
    // Always call fetchData if user is admin, regardless of permissions loading state
    // Also call if user has permission (hasPermission has fallback to role check)
    if (user && token) {
      // Role check removed - use permissions instead
      const hasPerm = hasPermission('admin.permissions');
      if (hasPerm) {
        fetchData();
      }
    }
  }, [user, permissions, token, hasPermission, fetchData]);

  const handleCreateTemplate = async () => {
    try {
      const permissionIds = templateForm.selectedPermissions;
      
      const response = await fetch(getApiUrl('/permissions/templates'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          templateCode: templateForm.templateCode,
          templateName: templateForm.templateName,
          description: templateForm.description,
          permissionIds,
          scheduleViewDepartments: templateForm.scheduleViewDepartments
        })
      });

      if (response.ok) {
        setSuccess(t('permissions.templates.errors.templateCreatedSuccessfully'));
        setCreateDialog(false);
        setTemplateForm({ ...emptyTemplateForm });
        fetchData();
      } else {
        const data = await response.json();
        setError(data.message || t('permissions.templates.errors.failedToCreateTemplate'));
      }
    } catch (err) {
      console.error('Error creating template:', err);
      setError(t('permissions.templates.errors.failedToCreateTemplate'));
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editDialog.template) return;

    try {
      const response = await fetch(getApiUrl(`/permissions/templates/${editDialog.template.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          templateName: templateForm.templateName,
          description: templateForm.description,
          permissionIds: templateForm.selectedPermissions,
          scheduleViewDepartments: templateForm.scheduleViewDepartments
        })
      });

      if (response.ok) {
        setSuccess(t('permissions.templates.errors.templateUpdatedSuccessfully'));
        setEditDialog({ open: false, template: null });
        setTemplateForm({ ...emptyTemplateForm });
        fetchData();
      } else {
        const data = await response.json();
        setError(data.message || t('permissions.templates.errors.failedToUpdateTemplate'));
      }
    } catch (err) {
      console.error('Error updating template:', err);
      setError(t('permissions.templates.errors.failedToUpdateTemplate'));
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await fetch(getApiUrl(`/permissions/templates/${templateId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess(t('permissions.templates.errors.templateDeletedSuccessfully'));
        fetchData();
      } else {
        const data = await response.json();
        setError(data.message || t('permissions.templates.errors.failedToDeleteTemplate'));
      }
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(t('permissions.templates.errors.failedToDeleteTemplate'));
    }
  };

  const handleEditClick = (template: PermissionTemplate) => {
    setTemplateForm({
      templateCode: template.template_code,
      templateName: template.template_name,
      description: template.description || '',
      selectedPermissions: template.permissions?.map(p => p.id) || [],
      scheduleViewDepartments: Array.isArray(template.schedule_view_departments)
        ? template.schedule_view_departments
        : []
    });
    setEditDialog({ open: true, template });
  };

  const scheduleDeptPicker = (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        {t(
          'permissions.templates.scheduleViewDepartments',
          'Darbo grafikas – papildomi skyriai (tik peržiūra)'
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t(
          'permissions.templates.scheduleViewDepartmentsHelp',
          'Nuosavas skyrius visada matomas. Čia pasirinkti skyriai leidžia tik peržiūrėti jų grafikus. schedule.view_all_departments vis tiek reiškia visus skyrius.'
        )}
      </Typography>
      <Autocomplete
        multiple
        options={availableDepartments}
        value={templateForm.scheduleViewDepartments}
        onChange={(_, value) =>
          setTemplateForm((prev) => ({ ...prev, scheduleViewDepartments: value }))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('permissions.templates.scheduleViewDepartmentsLabel', 'Papildomi skyriai')}
            placeholder={t('common.select', 'Pasirinkti')}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
          ))
        }
      />
    </Box>
  );

  const handlePermissionToggle = (permissionId: string) => {
    setTemplateForm(prev => ({
      ...prev,
      selectedPermissions: prev.selectedPermissions.includes(permissionId)
        ? prev.selectedPermissions.filter(id => id !== permissionId)
        : [...prev.selectedPermissions, permissionId]
    }));
  };

  // Show loading spinner while fetching
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Access check - allow admin users always, check permissions (hasPermission has fallback to role check)
  const hasAccess = hasPermission('admin.permissions');
  
  if (!hasAccess) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {t('permissions.templates.noAccess')}
        </Alert>
      </Box>
    );
  }

  // Group permissions by module
  const permissionsByModule = allPermissions.reduce((acc, perm) => {
    if (!acc[perm.module]) {
      acc[perm.module] = [];
    }
    acc[perm.module].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <SecurityIcon sx={{ mr: 2, fontSize: 32, color: '#1976d2' }} />
          <Typography variant="h4" component="h1">
            {t('permissions.templates.title')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setTemplateForm({ ...emptyTemplateForm });
            setCreateDialog(true);
          }}
        >
          {t('permissions.templates.createTemplate')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {templates.length === 0 && !loading && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('permissions.templates.noTemplatesFound')}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('permissions.templates.tableHeaders.templateName')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('permissions.templates.tableHeaders.code')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('permissions.templates.tableHeaders.description')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('permissions.templates.tableHeaders.permissions')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('permissions.templates.tableHeaders.scheduleViewDepts', 'Grafiko skyriai')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('permissions.templates.tableHeaders.type')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('permissions.templates.tableHeaders.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>{template.template_name}</TableCell>
                    <TableCell>
                      <Chip label={template.template_code} size="small" />
                    </TableCell>
                    <TableCell>{template.description || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {template.permissions?.length || 0} {t('permissions.templates.permissionsCount')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(template.schedule_view_departments || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(template.schedule_view_departments || []).map((dept) => (
                            <Chip key={dept} label={dept} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {template.is_system ? (
                        <Chip label={t('permissions.templates.system')} size="small" color="primary" />
                      ) : (
                        <Chip label={t('permissions.templates.custom')} size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('permissions.templates.editTemplate')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(template)}
                            disabled={template.is_system && !hasPermission('*.*')}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('permissions.templates.deleteTemplate')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTemplate(template.id)}
                            disabled={template.is_system && !hasPermission('*.*')}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Template Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('permissions.templates.createDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('permissions.templates.createDialog.templateCode')}
            fullWidth
            variant="outlined"
            value={templateForm.templateCode}
            onChange={(e) => setTemplateForm({ ...templateForm, templateCode: e.target.value })}
            sx={{ mb: 2 }}
            helperText={t('permissions.templates.createDialog.templateCodeHelper')}
          />
          <TextField
            margin="dense"
            label={t('permissions.templates.createDialog.templateName')}
            fullWidth
            variant="outlined"
            value={templateForm.templateName}
            onChange={(e) => setTemplateForm({ ...templateForm, templateName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('permissions.templates.createDialog.description')}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={templateForm.description}
            onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
            sx={{ mb: 2 }}
          />

          {scheduleDeptPicker}
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            {t('permissions.templates.createDialog.selectPermissions')}
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto', border: 1, borderColor: 'divider', p: 2, borderRadius: 1 }}>
            {Object.entries(permissionsByModule).map(([module, perms]) => (
              <Box key={module} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {module}
                </Typography>
                {perms.map(perm => (
                  <FormControlLabel
                    key={perm.id}
                    control={
                      <Checkbox
                        checked={templateForm.selectedPermissions.includes(perm.id)}
                        onChange={() => handlePermissionToggle(perm.id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{perm.permission_code}</Typography>
                        {perm.description && (
                          <Typography variant="caption" color="text.secondary">
                            {perm.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleCreateTemplate}
            variant="contained"
            disabled={!templateForm.templateCode || !templateForm.templateName}
          >
            {t('permissions.templates.createDialog.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, template: null })} maxWidth="md" fullWidth>
        <DialogTitle>{t('permissions.templates.editDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t('permissions.templates.editDialog.templateName')}
            fullWidth
            variant="outlined"
            value={templateForm.templateName}
            onChange={(e) => setTemplateForm({ ...templateForm, templateName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('permissions.templates.editDialog.description')}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={templateForm.description}
            onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
            sx={{ mb: 2 }}
          />

          {scheduleDeptPicker}
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            {t('permissions.templates.editDialog.selectPermissions')}
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto', border: 1, borderColor: 'divider', p: 2, borderRadius: 1 }}>
            {Object.entries(permissionsByModule).map(([module, perms]) => (
              <Box key={module} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {module}
                </Typography>
                {perms.map(perm => (
                  <FormControlLabel
                    key={perm.id}
                    control={
                      <Checkbox
                        checked={templateForm.selectedPermissions.includes(perm.id)}
                        onChange={() => handlePermissionToggle(perm.id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{perm.permission_code}</Typography>
                        {perm.description && (
                          <Typography variant="caption" color="text.secondary">
                            {perm.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, template: null })}>{t('common.cancel')}</Button>
          <Button
            onClick={handleUpdateTemplate}
            variant="contained"
            disabled={!templateForm.templateName}
          >
            {t('permissions.templates.editDialog.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionTemplates;

